<script setup lang="ts">
import { computed, inject, markRaw, Ref } from 'vue'
import { Copy, GripVertical, Lock, LockOpen, Settings, Trash2 } from '@lucide/vue'

import { useModalsStore } from '@/store/modals'

import {
  DSButton,
  DSConfirmationDialog,
  DSDropdownMenu,
  DSDropdownMenuContent,
  DSDropdownMenuItem,
  DSDropdownMenuLabel,
  DSDropdownMenuSeparator,
  DSDropdownMenuTrigger,
  DSInput,
} from '@ds'

type Props = {
  widgetType?: string
  name?: string
  protected?: boolean
  /** Show the Protect/Unprotect row. On by default; hide for a widget that carries no protectable state at all, or when the current user shouldn't see the control. */
  protectable?: boolean
  /** Discrete size flags this pattern resizes between — see the resize handles below. */
  wide?: boolean
  tall?: boolean
  /** Hide the body of the widget. */
  hideBody?: boolean
}

type Emits = {
  'update:name': [value: string]
  'update:protected': [value: boolean]
  'update:wide': [value: boolean]
  'update:tall': [value: boolean]
  duplicate: []
  delete: []
}

const {
  widgetType = '',
  name = '',
  protected: isProtected = false,
  protectable = true,
  wide = false,
  tall = false,
} = defineProps<Props>()
const emit = defineEmits<Emits>()

/*
 * There is no free-form drag-resize layout engine in this app — dashboards
 * lay widgets out with two boolean flags (wide/narrow, tall/short) that
 * `WidgetEditor.vue` binds straight to `widget.wide`/`widget.tall`. These
 * handles give the canonical drag affordance without a new sizing model: a
 * drag past RESIZE_THRESHOLD in a direction flips the corresponding flag,
 * same as the buttons it stands in for. Arrow keys do the same, so resizing
 * doesn't require a pointer.
 */
const RESIZE_THRESHOLD = 48

const modalsStore = useModalsStore()

const widgetView = inject<Ref<'mobile' | 'desktop'>>('widgetView')
const isMobile = computed(() => widgetView?.value === 'mobile')

function beginResize(axes: Array<'width' | 'height'>) {
  return (event: PointerEvent) => {
    event.preventDefault()
    const handle = event.currentTarget as HTMLElement
    handle.setPointerCapture(event.pointerId)
    const startX = event.clientX
    const startY = event.clientY
    let virtualWide = wide
    let virtualTall = tall

    const handleMove = (moveEvent: PointerEvent) => {
      if (axes.includes('width')) {
        const dx = moveEvent.clientX - startX
        const next = dx > RESIZE_THRESHOLD ? true : dx < -RESIZE_THRESHOLD ? false : virtualWide
        if (next !== virtualWide) {
          virtualWide = next
          emit('update:wide', virtualWide)
        }
      }
      if (axes.includes('height')) {
        const dy = moveEvent.clientY - startY
        const next = dy > RESIZE_THRESHOLD ? true : dy < -RESIZE_THRESHOLD ? false : virtualTall
        if (next !== virtualTall) {
          virtualTall = next
          emit('update:tall', virtualTall)
        }
      }
    }

    const handleUp = (upEvent: PointerEvent) => {
      handle.releasePointerCapture(upEvent.pointerId)
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
  }
}

function resizeKeydown(axes: Array<'width' | 'height'>) {
  return (event: KeyboardEvent) => {
    if (axes.includes('width') && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
      event.preventDefault()
      emit('update:wide', event.key === 'ArrowRight')
    }
    if (axes.includes('height') && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault()
      emit('update:tall', event.key === 'ArrowDown')
    }
  }
}

const handleDelete = () => {
  const modalInstance = modalsStore.init('widget-delete-confirmation-dialog', markRaw(DSConfirmationDialog))
  modalInstance?.open(modalsStore.getZIndex(), {
    title: `Delete widget "${name || 'Untitled'}"`,
    description: `"${name || 'Untitled'}" will be removed from this dashboard. This cannot be undone.`,
    label: 'Delete',
    onConfirm: () => {
      emit('delete')
    },
  })
}
</script>

<template>
  <div class="relative w-full h-full flex flex-col bg-card rounded-ds-lg border-[1.5px] border-primary ring-4 ring-primary/15 shadow-ds-lg">
    <div
      class="flex items-center gap-2 p-3"
      :class="hideBody ? '' : 'border-b border-border'"
    >
      <!--
        `.handle` is what `vuedraggable` (Dashboard.vue's reordering) already
        looks for via its `handle` option — kept as a forward-compat hook for
        when this pattern replaces the legacy widget chrome.
      -->
      <span
        title="Drag to move"
        class="handle flex items-center justify-center size-7.5 rounded-ds-sm text-muted-foreground cursor-grab hover:bg-muted hover:text-foreground"
      >
        <GripVertical class="size-4" />
      </span>
      <div class="flex-1 min-w-0 flex flex-col font-ds-caption">
        <div class="text-[11px] font-bold uppercase tracking-ds-caps text-muted-foreground">
          {{ widgetType }}
        </div>
        <DSInput
          :model-value="name"
          aria-label="Widget name"
          placeholder="Untitled widget"
          size="sm"
          class="h-auto px-2 -ml-2 border-transparent bg-transparent text-lg font-bold tracking-ds-tight font-ds-caption hover:border-border hover:bg-card"
          @update:model-value="emit('update:name', String($event))"
        />
      </div>
      <DSDropdownMenu>
        <DSDropdownMenuTrigger as-child>
          <DSButton
            type="button"
            variant="secondary"
            size="sm"
            title="Widget settings"
          >
            <Settings class="size-3.75" />
            Settings
          </DSButton>
        </DSDropdownMenuTrigger>
        <DSDropdownMenuContent
          align="end"
          class="w-60"
        >
          <template v-if="$slots.settings">
            <DSDropdownMenuLabel>Data</DSDropdownMenuLabel>
            <!--
              Bare outlet, no fallback — a per-widget filter, a special
              setting (e.g. this app's own "share metrics with related
              widgets" toggle, drawn by the widget's own Editor via
              `ShareProps.vue`, not this pattern), or anything else is a real
              feature only the caller knows the shape of. Omit the slot
              entirely to hide the whole "Data" section; a caller's own
              `DSDropdownMenuItem` row(s) render here when there's something
              to show.
            -->
            <slot name="settings" />
            <DSDropdownMenuSeparator />
          </template>
          <DSDropdownMenuLabel>Widget</DSDropdownMenuLabel>
          <DSDropdownMenuItem
            v-if="protectable"
            class="justify-between"
            @select="emit('update:protected', !isProtected)"
          >
            <span class="flex items-center gap-2">
              <component
                :is="isProtected ? LockOpen : Lock"
                class="size-4"
              />
              {{ isProtected ? 'Unprotect' : 'Protect' }}
            </span>
            <span class="text-xs text-muted-foreground">{{ isProtected ? 'Protected' : 'Unprotected' }}</span>
          </DSDropdownMenuItem>
          <DSDropdownMenuItem @select="emit('duplicate')">
            <Copy />
            Duplicate widget
          </DSDropdownMenuItem>
          <DSDropdownMenuSeparator />
          <DSDropdownMenuItem
            class="text-destructive focus:bg-destructive-subtle focus:text-destructive"
            @select="handleDelete"
          >
            <Trash2 />
            Delete widget
          </DSDropdownMenuItem>
        </DSDropdownMenuContent>
      </DSDropdownMenu>
    </div>
    <div
      v-if="!hideBody"
      class="min-h-70 flex-1 flex flex-col rounded-b-ds-lg overflow-hidden"
    >
      <slot>
        <div
          class="flex-1 py-5 px-6 min-h-0"
        >
          <div class="w-full h-full min-h-65 flex items-center justify-center border border-dashed border-input rounded-ds-md bg-background text-sm font-medium text-muted-foreground">
            Widget content area
          </div>
        </div>
      </slot>
      <template v-if="!isMobile">
        <button
          type="button"
          title="Drag to resize width"
          aria-label="Resize width, use arrow keys or drag"
          class="absolute top-1/2 -right-1.25 -mt-4 w-2.25 h-8 rounded-ds-sm bg-primary border-2 border-card shadow-ds-sm"
          :class="wide ? 'cursor-w-resize' : 'cursor-e-resize'"
          @pointerdown="beginResize(['width'])($event)"
          @keydown="resizeKeydown(['width'])($event)"
        />
        <button
          type="button"
          title="Drag to resize height"
          aria-label="Resize height, use arrow keys or drag"
          class="absolute -bottom-1.25 left-1/2 -ml-4 w-8 h-2.25 rounded-ds-sm bg-primary border-2 border-card shadow-ds-sm"
          :class="tall ? 'cursor-n-resize' : 'cursor-s-resize'"
          @pointerdown="beginResize(['height'])($event)"
          @keydown="resizeKeydown(['height'])($event)"
        />
        <button
          type="button"
          title="Drag to resize"
          aria-label="Resize, use arrow keys or drag"
          class="absolute -bottom-1.5 -right-1.5 size-3.5 rounded-ds-sm bg-primary border-2 border-card shadow-ds-sm"
          :class="wide && tall ? 'cursor-nw-resize' : wide ? 'cursor-se-resize' : tall ? 'cursor-ne-resize' : 'cursor-se-resize'"
          @pointerdown="beginResize(['width', 'height'])($event)"
          @keydown="resizeKeydown(['width', 'height'])($event)"
        />
      </template>
    </div>
  </div>
</template>
